import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { customersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  FiUsers, FiPlus, FiSearch, FiMail, FiPhone,
  FiEdit2, FiTrash2, FiChevronRight, FiBriefcase,
  FiUser, FiFilter
} from 'react-icons/fi';
import { toast } from 'sonner';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadCustomers();
  }, [searchQuery, selectedTag, selectedType]);

  const loadCustomers = async () => {
    try {
      const params = { search: searchQuery, tag: selectedTag };
      if (selectedType !== 'all') {
        params.client_type = selectedType;
      }
      const response = await customersAPI.getAll(params);
      const data = response.data?.customers || response.data?.clients || response.data || [];
      setCustomers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer ce client ?')) return;
    try {
      await customersAPI.delete(id);
      toast.success('Client supprimé');
      loadCustomers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Calculate stats
  const stats = {
    total: customers.length,
    particuliers: customers.filter(c => c.client_type === 'particulier' || !c.client_type).length,
    entreprises: customers.filter(c => c.client_type === 'entreprise' || c.client_type === 'association').length,
    withConsent: customers.filter(c => c.marketing_consent).length
  };

  // Get unique tags
  const allTags = [...new Set(customers.flatMap(c => c.tags || []))];

  // Filter customers
  const filteredCustomers = customers.filter(c => {
    if (selectedType === 'all') return true;
    if (selectedType === 'particulier') return c.client_type === 'particulier' || !c.client_type;
    if (selectedType === 'entreprise') return c.client_type === 'entreprise' || c.client_type === 'association';
    return true;
  });

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Base Clients (CRM)</h1>
          <p className="text-slate-500">Gérez votre base de contacts clients</p>
        </div>
        <Link to="/dashboard/customers/new">
          <Button className="bg-amber-600 hover:bg-amber-700">
            <FiPlus className="mr-2 h-4 w-4" /> Ajouter un client
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FiUsers className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-slate-500">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FiUser className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.particuliers}</p>
                <p className="text-sm text-slate-500">Particuliers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiBriefcase className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.entreprises}</p>
                <p className="text-sm text-slate-500">Entreprises</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiMail className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.withConsent}</p>
                <p className="text-sm text-slate-500">Opt-in marketing</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap bg-white p-4 rounded-lg border">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher par nom, email, entreprise..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="customer-search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <FiFilter className="h-4 w-4 text-slate-400" />
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger className="w-[180px]" data-testid="customer-type-filter">
              <SelectValue placeholder="Type de client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients</SelectItem>
              <SelectItem value="particulier">Particuliers</SelectItem>
              <SelectItem value="entreprise">Entreprises / Asso</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {allTags.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedTag === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedTag(null)}
            >
              Tous
            </Button>
            {allTags.slice(0, 5).map(tag => (
              <Button
                key={tag}
                variant={selectedTag === tag ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Customers List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent" />
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <FiUsers className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h3 className="font-medium text-slate-700 mb-2">Aucun client</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery || selectedType !== 'all' ? 'Aucun résultat pour ces critères' : 'Commencez par ajouter votre premier client'}
          </p>
          <Link to="/dashboard/customers/new">
            <Button>
              <FiPlus className="mr-2" /> Ajouter un client
            </Button>
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Client</th>
                <th className="text-left p-4 font-medium text-slate-600">Type</th>
                <th className="text-left p-4 font-medium text-slate-600">Contact</th>
                <th className="text-left p-4 font-medium text-slate-600">Tags</th>
                <th className="text-left p-4 font-medium text-slate-600">Marketing</th>
                <th className="text-right p-4 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map(customer => {
                const isEntreprise = customer.client_type === 'entreprise' || customer.client_type === 'association';
                return (
                  <tr 
                    key={customer.id} 
                    className="border-b hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/dashboard/customers/${customer.id}`)}
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${isEntreprise ? 'bg-amber-100' : 'bg-indigo-100'} rounded-full flex items-center justify-center`}>
                          {isEntreprise ? (
                            <FiBriefcase className="h-5 w-5 text-amber-600" />
                          ) : (
                            <span className="text-indigo-600 font-medium">
                              {(customer.first_name?.[0] || customer.email[0]).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 hover:text-indigo-600">
                            {isEntreprise && customer.company_name ? customer.company_name : `${customer.first_name} ${customer.last_name}`}
                          </p>
                          <p className="text-sm text-slate-500">{customer.email}</p>
                        </div>
                        <FiChevronRight className="h-4 w-4 text-slate-400 ml-auto" />
                      </div>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <Badge variant={isEntreprise ? 'default' : 'secondary'} className={isEntreprise ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' : ''}>
                        {isEntreprise ? 'Entreprise' : 'Particulier'}
                      </Badge>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      {customer.phone && (
                        <p className="text-sm flex items-center gap-1">
                          <FiPhone className="h-3 w-3" /> {customer.phone}
                        </p>
                      )}
                      {customer.city && (
                        <p className="text-sm text-slate-500">{customer.city}</p>
                      )}
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 flex-wrap">
                        {(customer.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {tag}
                          </span>
                        ))}
                        {(customer.tags?.length || 0) > 3 && (
                          <span className="text-xs text-slate-400">+{customer.tags.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      {customer.marketing_consent ? (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Oui</span>
                      ) : (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-1 rounded">Non</span>
                      )}
                    </td>
                    <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="p-2 hover:bg-slate-100 rounded" 
                        onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/customers/${customer.id}`); }}
                      >
                        <FiEdit2 className="h-4 w-4" />
                      </button>
                      <button 
                        className="p-2 hover:bg-red-100 rounded text-red-500" 
                        onClick={(e) => handleDeleteCustomer(customer.id, e)}
                      >
                        <FiTrash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
